import { CSSProperties, FC } from 'react'
import { Button, Col, Dropdown, MenuProps, Row, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  DiscordOutlined,
  FacebookOutlined,
  GlobalOutlined,
  InstagramOutlined,
  TikTokOutlined,
  XOutlined,
} from '@ant-design/icons'

const { Title, Paragraph } = Typography

const ThreadsIcon = () => (
  <svg viewBox="0 0 192 192" width="1em" height="1em" fill="currentColor">
    <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6071 106.601 81.2326 98.145 81.7522C73.3863 83.2983 57.3719 97.7617 58.5426 117.382C59.1394 127.128 63.7955 135.513 71.6527 140.882C78.3974 145.504 87.0449 147.822 96.0999 147.337C108.268 146.691 117.853 141.576 124.445 132.189C129.367 125.108 132.433 115.988 133.598 104.668C139.011 107.803 143.015 112.019 145.19 117.264C148.839 125.782 149.178 140.121 138.281 151.016C128.641 160.655 117.104 164.961 98.0141 165.109C77.2509 164.948 61.3339 158.667 50.6769 146.363C40.5599 134.662 35.2287 117.961 35.0006 97.0146C35.2287 76.0685 40.5599 59.3675 50.6769 47.6669C61.3339 35.3623 77.2509 29.0817 98.0141 28.9207C119.029 29.0861 135.153 35.4328 145.848 47.8134C151.07 53.8626 155.021 61.2674 157.685 69.8803L173.524 65.3983C170.125 54.3678 164.874 44.8046 157.671 36.9311C144.095 21.6433 124.583 13.6715 98.1109 13.4902C98.0789 13.49 98.0469 13.49 98.0141 13.49C97.9838 13.49 97.9527 13.49 97.9222 13.4902C71.4649 13.6709 52.0282 21.6093 38.5222 36.7882C25.7871 51.1139 19.2287 71.1527 19.0001 97.0088V97.0209C19.2287 122.877 25.7871 142.916 38.5222 157.242C52.0282 172.421 71.4649 180.359 97.9222 180.54C97.9527 180.54 97.9838 180.54 98.0141 180.54C98.0469 180.54 98.0789 180.54 98.1109 180.54C121.854 180.359 137.327 174.673 150.178 161.824C168.478 143.527 167.805 120.278 162.231 107.06C158.248 97.5869 150.885 89.9894 141.537 88.9883ZM97.4855 130.474C86.1042 131.125 77.3174 125.151 76.7014 117.779C76.2173 111.953 80.7273 104.614 96.6724 103.594C98.8269 103.463 100.944 103.398 103.017 103.398C109.168 103.398 114.904 103.927 120.152 104.946C118.243 124.758 108.891 129.857 97.4855 130.474Z" />
  </svg>
)

const shellContainerStyle: CSSProperties = {
  maxWidth: '1400px',
  margin: '0 auto',
  padding: '0 24px',
}

const iconLinks = [
  { href: 'https://www.facebook.com/wellchineseapp', icon: <FacebookOutlined />, title: 'Facebook' },
  { href: 'https://x.com/wellchineseapp', icon: <XOutlined />, title: 'Twitter' },
  { href: 'https://www.threads.com/@wellchinese', icon: <ThreadsIcon />, title: 'Threads' },
  { href: 'https://www.instagram.com/wellchinese', icon: <InstagramOutlined />, title: 'Instagram' },
  { href: 'https://www.tiktok.com/@wellchineseapp', icon: <TikTokOutlined />, title: 'TikTok' },
  { href: 'https://discord.gg/Njhhh6zMNN', icon: <DiscordOutlined />, title: 'Discord' },
]

interface AppFooterProps {
  languageMenuItems: MenuProps['items']
  onNavigate: (page: string) => void
}

const AppFooter: FC<AppFooterProps> = ({ languageMenuItems, onNavigate }) => {
  const { t, i18n } = useTranslation(['common', 'pages'])

  const navigationLinks = t('pages:footer.navigation.links', { returnObjects: true }) as Array<{ name: string; href: string }>
  const aboutLinks = t('pages:footer.about.links', { returnObjects: true }) as Array<{ name: string; href: string }>

  return (
    <footer className="footer-shell">
      <div className="shell-container" style={shellContainerStyle}>
        <Row gutter={[32, 32]}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <div className="footer-brand-block">
              <div className="footer-brand-row">
                <div className="brand-mark">
                  <img src="/images/logo.jpg" alt="WellChinese Logo" className="brand-logo" />
                </div>
                <span className="brand-text footer-brand-text">
                  {t('common:brand')}
                </span>
              </div>
              <Paragraph className="footer-copy">{t('pages:footer.desc')}</Paragraph>
              <a
                href="https://www.buymeacoffee.com/wellchinese"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-block', marginTop: '16px' }}
              >
                <img
                  src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                  alt="Buy Me a Coffee"
                  style={{ height: '60px', width: '217px' }}
                />
              </a>
            </div>
          </Col>

          <Col xs={12} sm={12} md={4} lg={4}>
            <Title level={5} className="footer-section-title">
              {t('pages:footer.navigation.title')}
            </Title>
            <div className="footer-link-group">
              {navigationLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="footer-link"
                  onClick={(e) => {
                    e.preventDefault()
                    if (link.href === '#resources-download') {
                      onNavigate('resources-download')
                    } else if (link.href === '#pinyin-learning') {
                      onNavigate('pinyin-module')
                    } else if (link.href === '#character-learning') {
                      onNavigate('character-goal')
                    } else if (link.href === '#chengyu-learning') {
                      onNavigate('chengyu-learning')
                    } else if (link.href === '#vocabulary') {
                      onNavigate('vocab-goal')
                    }
                  }}
                >
                  {link.name}
                </a>
              ))}
            </div>
          </Col>

          <Col xs={12} sm={12} md={4} lg={4}>
            <Title level={5} className="footer-section-title">
              {t('pages:footer.about.title')}
            </Title>
            <div className="footer-link-group">
              {aboutLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="footer-link"
                  onClick={(e) => {
                    if (link.href.startsWith('http')) {
                      return
                    }

                    e.preventDefault()
                    if (link.href === '#about') {
                      onNavigate('product-info')
                    } else if (link.href === '#terms') {
                      onNavigate('terms')
                    } else if (link.href === '#privacy') {
                      onNavigate('privacy')
                    } else if (link.href === '#contact') {
                      onNavigate('contact')
                    }
                  }}
                  target={link.href.startsWith('http') ? '_blank' : undefined}
                  rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                >
                  {link.name}
                </a>
              ))}
            </div>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <Title level={5} className="footer-section-title">
              {t('pages:footer.social.title')}
            </Title>
            <div className="footer-social-grid">
              {iconLinks.map((link) => (
                <a
                  key={link.title}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-link"
                  title={link.title}
                >
                  {link.icon}
                </a>
              ))}
            </div>

            <div className="footer-language-block">
              <Title level={5} className="footer-language-title">
                {t('common:language.title')}
              </Title>
              <Dropdown menu={{ items: languageMenuItems }} placement="topRight">
                <Button type="text" size="middle" className="footer-language-button">
                  <GlobalOutlined style={{ fontSize: '14px' }} />
                  <span style={{ fontSize: '14px' }}>{i18n.language === 'zh' ? '中文' : 'English'}</span>
                </Button>
              </Dropdown>
            </div>
          </Col>
        </Row>

        <div className="footer-bottom">
          <Paragraph className="footer-bottom-copy">
            {t('pages:footer.copyright', { year: new Date().getFullYear() })}
          </Paragraph>
        </div>
      </div>
    </footer>
  )
}

export default AppFooter
